import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Checkbox, Flex, FormControl, FormLabel, Heading,
  Input, Select, Spacer, Stack, Text, useToast
} from "@chakra-ui/react";

import { authFetch } from "../../utils/authFetch";

export default function DeviceManager() {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const isSystemAdmin = currentUser.role === "system-admin";

  const [devices, setDevices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [form, setForm] = useState({
    id: "",
    location: "",
    chartType: "bar",
    showAverage: true,
    showMax: false,
    showMin: false,
    customerId: "",
  });
  const [editingId, setEditingId] = useState(null);
  const API = process.env.REACT_APP_API_URL || "";

  const fetchDevices = async () => {
    const res = await authFetch(`${API}/api/devices`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setDevices(Array.isArray(data) ? data : []);
  };

  const fetchCustomers = async () => {
    if (!isSystemAdmin) return;
    const res = await authFetch(`${API}/api/customers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setCustomers(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    fetchDevices();
    fetchCustomers();
  }, []);

  const resetForm = () => {
    setForm({
      id: "",
      location: "",
      chartType: "bar",
      showAverage: true,
      showMax: false,
      showMin: false,
      customerId: "",
    });
    setEditingId(null);
    setFormVisible(false);
  };

  const validateForm = () => {
    if (!form.id || !form.location) {
      toast({ title: "Please fill in all required fields.", status: "warning" });
      return false;
    }
    if (isSystemAdmin && !form.customerId) {
      toast({ title: "Please select a customer.", status: "warning" });
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (!validateForm()) return;
    try {
      const res = await fetch(`${API}/api/devices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...form,
          customerId: isSystemAdmin ? form.customerId : currentUser.customerId,
        }),
      });
      if (!res.ok) throw new Error("Add failed");
      toast({ title: "Device added", status: "success" });
      resetForm();
      fetchDevices();
    } catch {
      toast({ title: "Failed to add device", status: "error" });
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    try {
      const res = await fetch(`${API}/api/devices/${editingId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...form,
          customerId: isSystemAdmin ? form.customerId : currentUser.customerId,
        }),
      });
      if (!res.ok) throw new Error("Update failed");
      toast({ title: "Device updated", status: "success" });
      resetForm();
      fetchDevices();
    } catch {
      toast({ title: "Update failed", status: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete this device?")) return;
    try {
      await fetch(`${API}/api/devices/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "Device deleted", status: "info" });
      fetchDevices();
    } catch {
      toast({ title: "Delete failed", status: "error" });
    }
  };

  return (
    <Flex justify="center" mt={10}>
      <Box p={10} w="70%" borderWidth={1} boxShadow={"lg"}>
        <Heading mb={4}>Manage Devices</Heading>

        {!formVisible && (
          <Button colorScheme="iBlue" mb={6} onClick={() => {
            setFormVisible(true);
            setEditingId(null);
            setForm({
              id: "",
              location: "",
              chartType: "bar",
              showAverage: true,
              showMax: false,
              showMin: false,
              customerId: "",
            });
          }}>Add Device</Button>
        )}

        {formVisible && (
          <Stack spacing={3} mb={6}>
            <FormControl isRequired>
              <FormLabel>Device ID (MN)</FormLabel>
              <Input
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value })}
                isDisabled={editingId !== null}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Location</FormLabel>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Chart Type</FormLabel>
              <Select
                value={form.chartType}
                onChange={(e) => setForm({ ...form, chartType: e.target.value })}
              >
                <option value="bar">Bar</option>
                <option value="line">Line</option>
              </Select>
            </FormControl>
            <Flex gap={4}>
              <Checkbox isChecked={form.showAverage} onChange={(e) => setForm({ ...form, showAverage: e.target.checked })}>Show Avg</Checkbox>
              <Checkbox isChecked={form.showMax} onChange={(e) => setForm({ ...form, showMax: e.target.checked })}>Show Max</Checkbox>
              <Checkbox isChecked={form.showMin} onChange={(e) => setForm({ ...form, showMin: e.target.checked })}>Show Min</Checkbox>
            </Flex>
            {isSystemAdmin && (
              <FormControl isRequired>
                <FormLabel>Customer</FormLabel>
                <Select
                  value={form.customerId}
                  onChange={(e) => setForm({ ...form, customerId: e.target.value })}
                >
                  <option value="">Select customer</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              </FormControl>
            )}
            <Flex gap={4}>
              <Button colorScheme="iTeal" onClick={() => (editingId ? handleUpdate() : handleAdd())}>Save</Button>
              <Button onClick={resetForm} colorScheme="iRed">Cancel</Button>
              {editingId && (
                <Button colorScheme="iOrange" onClick={() => handleDelete(editingId)}>Delete</Button>
              )}
            </Flex>
          </Stack>
        )}

        <Heading size="md" mb={4}>Device List</Heading>
        <Stack spacing={3}>
          {devices.map((d) => (
            <Box key={d.id} borderWidth={1} borderRadius="md" p={4}>
              <Flex>
                <Box>
                  <Text fontWeight="bold">{d.id}</Text>
                  <Text fontSize="sm">{d.location}</Text>
                  <Text fontSize="sm">Chart: {d.chartType}, Avg: {d.showAverage ? "✔" : "✘"}, Max: {d.showMax ? "✔" : "✘"}, Min: {d.showMin ? "✔" : "✘"}</Text>
                </Box>
                <Spacer />
                <Button size="sm" colorScheme="iBlue" mr={2} onClick={() => {
                  setEditingId(d.id);
                  setForm({ ...d });
                  setFormVisible(true);
                }}>Edit</Button>
                
              </Flex>
            </Box>
          ))}
        </Stack>

        <Flex justify="center" mt={8}>
          <Button onClick={() => navigate("/")} colorScheme="gray" variant="outline">Back to Menu</Button>
        </Flex>
      </Box>
    </Flex>
  );
}